import React from 'react';
import PropTypes from 'prop-types';
import styled, {css, keyframes} from 'styled-components';
import {Portal} from 'react-portal';
import FocusTrap from 'focus-trap-react';
import Overlay from '@nib-components/overlay';
import Heading from '@nib-components/heading';
import {focusOutlineColor, focusOutlineStyle, focusOutlineWidth, focusOutlineOffset, copyFontFamily, copyColor, colorDarkest, colorWhite, boxShadowStandard} from '@nib-components/theme';
import {CloseSystemIcon} from '@nib/icons';
import {Columns, Column, Container, breakpoint, p, px, pt, py, pr, pb, m, mb, mt} from '@nib/layout';

export interface BaseModalProps {
  title?: React.ReactNode;
  visible?: boolean;
  closeOnEsc?: boolean;
  closeOnClick?: boolean;
  onClose?: () => void;
  invert?: boolean;
  variation?: string;
  collapseTitle?: boolean;
  children?: React.ReactNode;
  [key: string]: unknown;
}

export interface ContentModalProps extends BaseModalProps {
  variation: 'content';
  stickyHeader?: boolean;
}

export type ModalProps = BaseModalProps | ContentModalProps;

export interface ModalWrapperProps {
  invert?: boolean;
  variation?: string;
  [key: string]: unknown;
}

export interface ModalHeaderProps {
  id: string;
  variation?: boolean;
  stickyHeader?: boolean;
  collapseTitle?: boolean;
  [key: string]: unknown;
}

const ModalOverlay = styled(Overlay)<{variation?: ModalProps['variation']}>`
  /* non-IE styling */
  @supports (display: flex) {
    display: flex;
    justify-content: center;
  }

  cursor: pointer;
  z-index: 999;

  ${({variation}) =>
    variation === 'dialog' &&
    css`
      ${breakpoint('md')`
        ${py(7)};
      `};
    `};
`;

const slideUp = keyframes`
  from {
    transform: translateY(10%);
  }

  to {
    transform: translateY(0);
  }
`;

export const ModalWrapper = styled.div<ModalWrapperProps>`
  font-family: ${copyFontFamily || 'sans-serif'};
  box-sizing: border-box;
  cursor: default;
  width: 100%;
  margin: auto;
  margin-bottom: 0;
  box-shadow: ${boxShadowStandard || '0 0 5px rgba(0, 0, 0, 0.2)'};
  animation: 0.2s cubic-bezier(0, 0, 0, 1) ${slideUp};

  ${({variation}) =>
    variation === 'content'
      ? css`
          max-width: 1200px;

          ${breakpoint('md')`
            ${m(6)};
            overflow: auto;
          `};
        `
      : css`
          ${breakpoint('md')`
            max-width: 620px;
            margin-top: 0;
            margin-bottom: auto;
          `};
        `};

  /* Light and dark themes via the invert prop */
  color: ${props => (props.invert ? colorWhite : copyColor)};
  background-color: ${props => (props.invert ? colorDarkest : colorWhite)};
`;

export const ModalHeader = styled.header<ModalHeaderProps>`
  outline: none;

  ${({stickyHeader}) =>
    stickyHeader
      ? css`
          @supports (position: sticky) {
            position: sticky;
            background-color: ${colorWhite};
            top: 0;
            z-index: 999;
            box-shadow: ${boxShadowStandard};
          }

          ${({collapseTitle}: any) =>
            collapseTitle
              ? css`
                  ${px(6)};
                  ${py(2)};

                  ${breakpoint('md')`
                    ${px(6)};
                    ${py(4)};
                  `};
                `
              : css`
                  ${px(6)};
                  ${py(4)};
                `};
        `
      : css`
          ${({collapseTitle}: any) =>
            collapseTitle
              ? css`
                  ${p(6)};
                  ${pb(2)};

                  ${breakpoint('md')`
                    ${p(6)};
                  `};
                `
              : css`
                  ${p(6)};
                `};
        `};
`;

export const ModalTitleAbove = styled(Heading)<{collapseTitle?: boolean}>`
  ${pr(2)};

  ${({collapseTitle}) =>
    collapseTitle &&
    css`
      display: none;

      ${breakpoint('md')`
        display: block;
      `};
    `};
`;

export const ModalTitleInside = styled(Heading)<{stickyHeader?: boolean}>`
  ${mb(6)};

  ${breakpoint('md')`
    ${mb(0)};
    display: none;
  `};

  ${({stickyHeader}) =>
    stickyHeader &&
    css`
      ${mt(6)};
    `};
`;

export const ModalSection = styled.section<{
  collapseTitle?: boolean;
  stickyHeader?: boolean | unknown;
}>`
  ${p(6)};

  ${({collapseTitle}) =>
    collapseTitle &&
    css`
      ${pt(0)};
    `};

  ${({stickyHeader}) =>
    !stickyHeader &&
    css`
      ${pt(0)};
    `};

  ${breakpoint('md')`
    ${p(6)};

    ${({stickyHeader}: {stickyHeader?: boolean}) =>
      !stickyHeader &&
      css`
        ${pt(0)};
      `};
  `};
`;

export const ModalClose = styled.button<ModalProps>`
  appearance: none;
  background: none;
  border: none;

  ${p(1)};
  cursor: pointer;

  color: ${props => (props.invert ? colorWhite : colorDarkest)};

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: ${focusOutlineOffset};
  }
`;

// FocusTrap requires an id string to set the initialFocus. We are hardcoding this here because there should only ever be one modal open at a time, and when closed the modal is unmounted from the dom
// Potential improvement would be to use something like `shortid` to generate this id on mount, or use a ref instead.
// Both were explored and did not seem to play nicely with FocusTrap :(
const POTENTIALLY_DANGEROUS_HARDCODED_ID = 'potentially-dangerous-hardcoded-id-for-focused-modal-wrapper';

const Modal: React.FC<ModalProps> = props => {
  const {title, visible, variation = 'dialog', onClose, children, invert, collapseTitle, stickyHeader, ...otherProps} = props;
  /*
    We set the outer (portal) div to be always open, otherwise it shuts instantly and we never get to see the inner div (the modal) close
    the reason we want to see the inner div close, is that it has a fadeout on it.
    If we had a simple event to fire when the model has stopped fading then we could call the closePortal from that.
  */
  const ALWAYS_OPEN = true;
  return (
    <Portal isOpened={ALWAYS_OPEN}>
      <ModalOverlay visible={visible} onClose={onClose} mode={invert ? 'light' : 'dark'} variation={variation} {...otherProps}>
        <ModalWrapper invert={invert} variation={variation} role="dialog" aria-label={title} aria-describedby="modalBody">
          {/*
            FocusTrap handles both:
              - trapping focus to within the open modal, and
              - returning focus to the element that triggered the Modal opening, after closing
          */}
          <FocusTrap
            focusTrapOptions={{
              clickOutsideDeactivates: true,
              initialFocus: `#${POTENTIALLY_DANGEROUS_HARDCODED_ID}`
            }}
          >
            <div>
              <ModalHeader tabIndex="-1" id={POTENTIALLY_DANGEROUS_HARDCODED_ID} variation={variation} stickyHeader={variation === 'content' && stickyHeader} collapseTitle={collapseTitle}>
                <Columns>
                  <Column>
                    <ModalTitleAbove size={3} collapseTitle={collapseTitle}>
                      {title}
                    </ModalTitleAbove>
                  </Column>
                  <Column width="content" style={{fontSize: 0}}>
                    <ModalClose invert={invert} onClick={onClose} aria-label="Close this dialog window">
                      <CloseSystemIcon />
                    </ModalClose>
                  </Column>
                </Columns>
              </ModalHeader>

              <ModalSection collapseTitle={collapseTitle} stickyHeader={variation === 'content' && stickyHeader}>
                {collapseTitle && (
                  <ModalTitleInside size={3} stickyHeader={variation === 'content' && stickyHeader}>
                    {title}
                  </ModalTitleInside>
                )}
                <div id="modalBody">{variation === 'content' ? <Container>{children}</Container> : <>{children}</>}</div>
              </ModalSection>
            </div>
          </FocusTrap>
        </ModalWrapper>
      </ModalOverlay>
    </Portal>
  );
};

Modal.displayName = 'Modal';

Modal.propTypes = {
  title: PropTypes.node,
  visible: PropTypes.bool,
  variation: PropTypes.string,
  closeOnEsc: PropTypes.bool,
  closeOnClick: PropTypes.bool,
  onClose: PropTypes.func,
  invert: PropTypes.bool,
  collapseTitle: PropTypes.bool,
  stickyHeader: PropTypes.bool,
  children: PropTypes.node
};

Modal.defaultProps = {
  visible: false,
  variation: 'dialog',
  invert: false,
  collapseTitle: true,
  stickyHeader: false
};

export default Modal;
